import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
    wechatLogin,
    checkLogin,
    getCurrentUser,
    logout as logoutService,
    getWeChatOpenIdFromUrl,
    UserInfo
} from '../services/wechatAuthService';

interface AuthContextType {
    isLoading: boolean;
    isLoggedIn: boolean;
    userInfo: UserInfo | null;
    weChatOpenId: string | null;
    login: () => Promise<boolean>;
    logout: () => Promise<void>;
    refreshUserInfo: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [weChatOpenId] = useState<string | null>(() => {
        // 优先从URL获取，其次从localStorage获取
        const urlOpenId = getWeChatOpenIdFromUrl();
        console.log('初始化 weChatOpenId - 从URL获取:', urlOpenId);

        if (urlOpenId) {
            // 如果URL中有openId，保存到localStorage
            localStorage.setItem('weChatOpenId', urlOpenId);
            console.log('保存 weChatOpenId 到 localStorage:', urlOpenId);
            return urlOpenId;
        }

        // 从localStorage获取
        const storedOpenId = localStorage.getItem('weChatOpenId');
        console.log('从 localStorage 获取 weChatOpenId:', storedOpenId);
        return storedOpenId;
    });

    // 初始化认证状态
    useEffect(() => {
        initializeAuth();
    }, []);

    const initializeAuth = async () => {
        try {
            setIsLoading(true);
            console.log('=== 开始初始化认证状态 ===');
            console.log('weChatOpenId:', weChatOpenId);

            // 1. 如果有微信OpenId，检查登录状态
            if (weChatOpenId) {
                console.log('检查登录状态，weChatOpenId:', weChatOpenId);
                const loginStatus = await checkLogin(weChatOpenId);
                console.log('登录状态检查结果:', loginStatus);

                if (loginStatus.success && loginStatus.loggedIn && loginStatus.userInfo) {
                    console.log('用户已登录，设置用户信息');
                    setUserInfo(loginStatus.userInfo);
                    setIsLoggedIn(true);
                    setIsLoading(false);
                    return;
                }

                // 2. 如果未登录，不自动尝试登录，让用户先提交资料
                console.log('用户未登录，需要先提交资料');
                setIsLoggedIn(false);
                setUserInfo(null);
            } else {
                // 没有微信OpenId，设置为未登录状态
                console.log('没有微信OpenId，设置未登录状态');
                setIsLoggedIn(false);
                setUserInfo(null);
            }

            console.log('=== 认证状态初始化完成 ===');
        } catch (error) {
            console.error('初始化认证状态失败:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const performWechatLogin = async (): Promise<boolean> => {
        if (!weChatOpenId) {
            console.log('没有微信OpenId，无法登录');
            return false;
        }

        try {
            console.log('开始微信登录，OpenId:', weChatOpenId);
            const result = await wechatLogin(weChatOpenId);
            console.log('微信登录API响应:', result);

            if (result.success && result.userInfo) {
                console.log('微信登录成功，设置用户信息');
                setIsLoggedIn(true);
                setUserInfo(result.userInfo);
                return true;
            } else {
                console.log('微信登录失败:', result.message);
                console.log('设置登录状态为false');
                setIsLoggedIn(false);
                setUserInfo(null);
                return false;
            }
        } catch (error) {
            console.error('微信登录过程中出错:', error);
            setIsLoggedIn(false);
            setUserInfo(null);
            return false;
        }
    };

    const login = async (): Promise<boolean> => {
        return await performWechatLogin();
    };

    const logout = async (): Promise<void> => {
        try {
            await logoutService();
            setIsLoggedIn(false);
            setUserInfo(null);
            
            // 注销时清理除了weChatOpenId之外的localStorage数据
            // 保留weChatOpenId以便重新登录
            const savedWeChatOpenId = localStorage.getItem('weChatOpenId');
            
            // 清理可能的敏感数据，但保留weChatOpenId
            localStorage.removeItem('wechat_user');
            localStorage.removeItem('wechat_token');
            localStorage.removeItem('wechat_token_expiry');
            
            // 如果有weChatOpenId，重新保存
            if (savedWeChatOpenId) {
                localStorage.setItem('weChatOpenId', savedWeChatOpenId);
            }
            
            console.log('登出成功');
        } catch (error) {
            console.error('登出失败:', error);
        }
    };

    const refreshUserInfo = async (): Promise<void> => {
        try {
            const result = await getCurrentUser();
            if (result.success && result.loggedIn && result.userInfo) {
                setUserInfo(result.userInfo);
                setIsLoggedIn(true);
            } else {
                setUserInfo(null);
                setIsLoggedIn(false);
            }
        } catch (error) {
            console.error('刷新用户信息失败:', error);
        }
    };

    const value: AuthContextType = {
        isLoading,
        isLoggedIn,
        userInfo,
        weChatOpenId,
        login,
        logout,
        refreshUserInfo,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
